import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { BASE_URL, GET_OP_REPORT, GET_PATIENTS } from '../utlilities/config';
import { 
  FiUser, FiCalendar, FiSearch, FiArrowRight,
  FiMapPin, FiPhone, FiClock, FiEdit2, FiDownload, FiPlus, FiArrowLeft
} from "react-icons/fi";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import AddPatientModal from '../components/AddPatientModal';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { TabContext } from '../components/TabbedLayout';
import withAuth from '../common/auth';

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading patients...</p>
    </div>
  </div>
);

const PatientCard = ({ patient, onClick, onEdit }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleEditClick = (e) => {
    e.stopPropagation(); // Prevent card click when clicking edit
    if (onEdit) {
      onEdit(patient);
    }
  };

  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-400 p-4 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all duration-200 cursor-pointer group relative"
    >
      {/* Edit Button */}
      <button
        onClick={handleEditClick}
        className="absolute top-3 right-3 p-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors opacity-0 group-hover:opacity-100 z-10"
        title="Edit Patient"
      >
        <FiEdit2 size={16} />
      </button>

      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-md shadow-md">
            {patient.PatientName?.charAt(0)?.toUpperCase() || 'P'}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-black text-sm truncate group-hover:text-indigo-600 transition-colors">
              {patient.PatientName || 'Unknown'}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {(patient.Age !== undefined || patient.Month !== undefined || patient.Days !== undefined) && (
                <span className="text-xs text-black bg-gray-300 px-2 py-0.5 rounded-full">
                  {[
                    patient.Age ? `${patient.Age}Y` : '',
                    patient.Month ? `${patient.Month}M` : '',
                    patient.Days ? `${patient.Days}D` : ''
                  ].filter(Boolean).join(' ') || 'N/A'}
                </span>
              )}
              {patient.Gender && (
                <span className="text-xs text-black bg-gray-300 px-2 py-0.5 rounded-full">
                  {patient.Gender}
                </span>
              )}
            </div>
          </div>
        </div>
        <FiArrowRight className="text-black group-hover:text-indigo-600 transition-colors" size={20} />
      </div>

      <div className="space-y-2 border-t border-gray-100 pt-3">
        {patient.MobileNo && (
          <div className="flex items-center gap-2 text-xs text-black">
            <FiPhone size={14} className="text-indigo-500" />
            <span>{patient.MobileNo}</span>
          </div>
        )}
        
        {patient.Place && (
          <div className="flex items-center gap-2 text-xs text-black">
            <FiMapPin size={14} className="text-indigo-500" />
            <span className="truncate">{patient.Place}</span>
          </div>
        )}

        {patient.EntryDate && (
          <div className="flex items-center gap-2 text-xs text-black">
            <FiCalendar size={14} className="text-indigo-500" />
            <span>{formatDate(patient.EntryDate)}</span>
          </div>
        )}

        {patient.Ref_DoctorName && (
          <div className="flex items-center gap-2 text-xs text-black">
            <FiUser size={14} className="text-indigo-500" />
            <span className="truncate">{patient.Ref_DoctorName}</span>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-black">OP Number:</span>
            <span className="text-xs font-bold text-indigo-600">{patient.InvoiceNo || 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '36px',
    height: '36px',
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
    height: '36px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '36px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
      ? '#92c3fcff'
      : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    padding: '6px 8px',
    fontSize: '0.85rem',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '0.9rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '0.9rem',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

// Helper function to get local date string (IST)
const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function Patients() {
  const tabContext = useContext(TabContext);
  const token = localStorage.getItem('accessToken');
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getLocalDateString());
  const [toDate, setToDate] = useState(getLocalDateString());
  const [patients, setPatients] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [patientModal, setPatientModal] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [selectedSearchPatient, setSelectedSearchPatient] = useState(null);
  const [pageno] = useState(1);
  const [pagesize] = useState(100);

  useEffect(() => {
    getPatientsData();
  }, []);

  const fetchPatientFromOPReport = async (patientId) => {
    try {
      // Fetch with a wider date range (last year) to find the patient
      const today = new Date();
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);
      
      const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const response = await axios.get(`${BASE_URL}${GET_OP_REPORT}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'fromDate': formatDateForAPI(oneYearAgo),
          'toDate': formatDateForAPI(today),
        },
      });

      if (response.status === 200) {
        const responseData = response.data.data || [];
        // Find patient by PatientID
        const patientIdNum = parseInt(patientId);
        const patient = responseData.find(p => {
          const pId = p.PatientID;
          return pId === patientIdNum || 
                 pId === patientId ||
                 String(pId) === String(patientId) ||
                 String(pId) === String(patientIdNum);
        });
        
        return patient;
      }
    } catch (error) {
      console.error('Error fetching patient from OP Report:', error);
      return null;
    }
  };

  // When originalData changes (after date range search), check if selected patient exists
  useEffect(() => {
    if (selectedSearchPatient && originalData.length > 0) {
      const existsInCurrentRange = originalData.some(
        p => p.PatientID === selectedSearchPatient.PatientID
      );
      
      // If patient doesn't exist in new date range, fetch their data
      if (!existsInCurrentRange) {
        const fetchAndAddPatient = async () => {
          setLoading(true);
          try {
            const patientData = await fetchPatientFromOPReport(selectedSearchPatient.PatientID);
            
            if (patientData) {
              const updatedData = [...originalData, patientData];
              setOriginalData(updatedData);
            } else {
              // If still not found, add patient from search results
              const patientFromSearch = {
                PatientID: selectedSearchPatient.PatientID,
                PatientName: selectedSearchPatient.PatientName,
                Age: selectedSearchPatient.Age,
                Gender: selectedSearchPatient.Gender,
                MobileNo: selectedSearchPatient.MobileNo,
                Place: selectedSearchPatient.Place,
                Month: selectedSearchPatient.Month,
                Days: selectedSearchPatient.Days,
                EntryDate: null,
                InvoiceNo: null,
                Ref_DoctorName: null
              };
              const updatedData = [...originalData, patientFromSearch];
              setOriginalData(updatedData);
            }
          } catch (error) {
            console.error('Error fetching patient data:', error);
            // Even if fetch fails, show the patient from search results
            const patientFromSearch = {
              PatientID: selectedSearchPatient.PatientID,
              PatientName: selectedSearchPatient.PatientName,
              Age: selectedSearchPatient.Age,
              Gender: selectedSearchPatient.Gender,
              MobileNo: selectedSearchPatient.MobileNo,
              Place: selectedSearchPatient.Place,
              Month: selectedSearchPatient.Month,
              Days: selectedSearchPatient.Days,
              EntryDate: null,
              InvoiceNo: null,
              Ref_DoctorName: null
            };
            const updatedData = [...originalData, patientFromSearch];
            setOriginalData(updatedData);
          } finally {
            setLoading(false);
          }
        };
        
        fetchAndAddPatient();
      }
    }
  }, [originalData, selectedSearchPatient]);

  useEffect(() => {
    filterPatients();
  }, [searchQuery, originalData, selectedSearchPatient]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return dateString;
  };

  const getPatientsData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_OP_REPORT}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json', 
          'fromDate': formatDate(fromDate),
          'toDate': formatDate(toDate),
        },
      });

      if (response.status === 200) {
        const responseData = response.data.data || [];
        console.log(responseData);
        
        // Group by PatientID to get unique patients
        const uniquePatientsMap = new Map();
        responseData.forEach(patient => {
          const patientId = patient.PatientID;
          if (!uniquePatientsMap.has(patientId)) {
            uniquePatientsMap.set(patientId, patient);
          } else {
            // If multiple visits, keep the most recent one
            const existing = uniquePatientsMap.get(patientId);
            const existingDate = new Date(existing.EntryDate);
            const currentDate = new Date(patient.EntryDate);
            if (currentDate > existingDate) {
              uniquePatientsMap.set(patientId, patient);
            }
          }
        });

        const uniquePatients = Array.from(uniquePatientsMap.values());
        setOriginalData(uniquePatients);
        setPatients(uniquePatients);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error('Failed to fetch patients data.', {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterPatients = () => {
    // If a patient is selected from search, show only that patient
    if (selectedSearchPatient) {
      const filtered = originalData.filter(patient => 
        patient.PatientID === selectedSearchPatient.PatientID
      );
      setPatients(filtered);
      return;
    }

    // Otherwise, use the regular search query
    if (!searchQuery.trim()) {
      setPatients(originalData);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = originalData.filter(patient => {
      const name = (patient.PatientName || '').toLowerCase();
      const mobile = (patient.MobileNo || '').toLowerCase();
      const place = (patient.Place || '').toLowerCase();
      const doctor = (patient.Ref_DoctorName || '').toLowerCase();
      const opNumber = (patient.InvoiceNo || '').toString();

      return name.includes(query) || 
             mobile.includes(query) || 
             place.includes(query) || 
             doctor.includes(query) ||
             opNumber.includes(query);
    });

    setPatients(filtered);
  };

  const handleMobileChange = async (inputValue) => {
    if (inputValue.length === 3 || inputValue.length === 6 || inputValue.length === 9 || inputValue.length === 10) {
      await getFilterData(inputValue);
    } else if (inputValue.length === 0) {
      setPatientSuggestions([]);
      setSelectedSearchPatient(null);
    }
  };

  const getFilterData = async (inputValue) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_PATIENTS}`, {
        headers: {
          'PageNo': pageno,
          'PageSize': pagesize,
          'FilterText': inputValue,
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200) {
        const masterData = response.data.data?.masterData || [];
        setPatientSuggestions(masterData);
      }
    } catch (error) {
      console.error('Error filtering patients:', error);
      toast.error('Failed to filter patients.', {
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = async (selectedOption) => {
    if (selectedOption) {
      setSelectedSearchPatient(selectedOption);
      setSearchQuery(''); // Clear text search when patient is selected
      
      // Check if patient exists in current date range
      const existsInCurrentRange = originalData.some(
        p => p.PatientID === selectedOption.PatientID
      );
      
      // If patient doesn't exist in current date range, fetch their data
      if (!existsInCurrentRange) {
        setLoading(true);
        try {
          const patientData = await fetchPatientFromOPReport(selectedOption.PatientID);
          
          if (patientData) {
            // Add patient to originalData temporarily for display
            const updatedData = [...originalData, patientData];
            setOriginalData(updatedData);
          } else {
            // If still not found, create a patient object from the search result
            // This ensures the patient card is shown even without OP visit data
            const patientFromSearch = {
              PatientID: selectedOption.PatientID,
              PatientName: selectedOption.PatientName,
              Age: selectedOption.Age,
              Gender: selectedOption.Gender,
              MobileNo: selectedOption.MobileNo,
              Place: selectedOption.Place,
              Month: selectedOption.Month,
              Days: selectedOption.Days,
              EntryDate: null, // No recent visit
              InvoiceNo: null,
              Ref_DoctorName: null
            };
            const updatedData = [...originalData, patientFromSearch];
            setOriginalData(updatedData);
          }
        } catch (error) {
          console.error('Error fetching patient data:', error);
          // Even if fetch fails, show the patient from search results
          const patientFromSearch = {
            PatientID: selectedOption.PatientID,
            PatientName: selectedOption.PatientName,
            Age: selectedOption.Age,
            Gender: selectedOption.Gender,
            MobileNo: selectedOption.MobileNo,
            Place: selectedOption.Place,
            Month: selectedOption.Month,
            Days: selectedOption.Days,
            EntryDate: null,
            InvoiceNo: null,
            Ref_DoctorName: null
          };
          const updatedData = [...originalData, patientFromSearch];
          setOriginalData(updatedData);
        } finally {
          setLoading(false);
        }
      }
    } else {
      setSelectedSearchPatient(null);
    }
  };

  const customOptionLabel = ({ PatientName, Age, Place, MobileNo, Month, Days }) => {
    const isValidValue = (value) => {
      return value !== null && value !== undefined && value !== '';
    };
  
    return (
      <div className="flex items-center space-x-3 py-1">
        {isValidValue(PatientName) && (
          <div className="font-semibold text-sm text-black">
            {PatientName}
          </div>
        )}
        {isValidValue(Age) && (
          <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">
            {Age} years
          </div>
        )}
        {isValidValue(Month) && (
          <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">
            {Month} months
          </div>
        )}
        {isValidValue(Days) && (
          <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">
            {Days} days
          </div>
        )}
        {isValidValue(Place) && (
          <div className="text-xs text-black flex items-center">
            <FiMapPin className="mr-1" size={12} />
            {Place}
          </div>
        )}
        {isValidValue(MobileNo) && (
          <div className="text-xs text-black flex items-center">
            <FiPhone className="mr-1" size={12} />
            {MobileNo}
          </div>
        )}
      </div>
    );
  };

  const patientOptions = patientSuggestions.map(patient => ({
    value: patient.PatientID,
    label: `${patient.PatientName} - ${patient.Age} Y - ${patient.Month} M - ${patient.Days} D - ${patient.Place} - ${patient.MobileNo}`,
    ...patient
  }));

  const handlePatientClick = (patient) => {
    // Open patient details in a new tab
    if (tabContext && tabContext.openTab) {
      // Store patient data in sessionStorage to pass to PatientDetails
      sessionStorage.setItem(`patient_${patient.PatientID}`, JSON.stringify(patient));
      tabContext.openTab(`patientdetails/${patient.PatientID}`, `Patient: ${patient.PatientName}`);
    }
  };

  const handleSearch = () => {
    getPatientsData();
  };

  const handleEditPatient = (patient) => {
    setEditPatient(patient);
    setPatientModal(true);
  };

  const handleAddNewPatient = () => {
    setEditPatient(null);
    setPatientModal(true);
  };

  const handlePatientUpdated = (updatedPatient) => {
    // Refresh patient data after update
    getPatientsData();
    setPatientModal(false);
    setEditPatient(null);
    if (updatedPatient) {
      toast.success('Patient added successfully', { autoClose: 2000 });
    } else {
      toast.success('Patient updated successfully', { autoClose: 2000 });
    }
  };

  const refreshPatientData = async () => {
    // This will be called by the modal after patient is saved/updated
    await getPatientsData();
  };

  const formatDateForExport = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const exportToExcel = () => {
    try {
      if (!patients || patients.length === 0) {
        toast.error('No patient records to export', { autoClose: 2000 });
        return;
      }

      // Prepare data for Excel export
      const excelData = patients.map(patient => ({
        'Patient ID': patient.PatientID || 'N/A',
        'Patient Name': patient.PatientName || 'N/A',
        'Mobile Number': patient.MobileNo || 'N/A',
        'Place': patient.Place || 'N/A',
        'Age': patient.Age || 0,
        'Month': patient.Month || 0,
        'Days': patient.Days || 0,
        'Date of Birth': patient.DOB ? formatDateForExport(patient.DOB) : 'N/A',
        'Gender': patient.Gender || 'N/A',
        'Entry Date': patient.EntryDate ? formatDateForExport(patient.EntryDate) : 'N/A',
        'OP Number': patient.InvoiceNo || 'N/A',
        'Doctor Name': patient.Ref_DoctorName || 'N/A',
        'Valid Upto': patient.ValidUpto ? formatDateForExport(patient.ValidUpto) : 'N/A',
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 12 }, // Patient ID
        { wch: 25 }, // Patient Name
        { wch: 15 }, // Mobile Number
        { wch: 20 }, // Place
        { wch: 8 },  // Age
        { wch: 8 },  // Month
        { wch: 8 },  // Days
        { wch: 15 }, // Date of Birth
        { wch: 10 }, // Gender
        { wch: 15 }, // Entry Date
        { wch: 12 }, // OP Number
        { wch: 25 }, // Doctor Name
        { wch: 15 }, // Valid Upto
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Patients');

      // Generate filename with date range
      const fromDateStr = fromDate ? new Date(fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
      const toDateStr = toDate ? new Date(toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
      const filename = `Patients_Report_${fromDateStr}_to_${toDateStr}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      toast.success('Excel file exported successfully', { autoClose: 2000 });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel', { autoClose: 2000 });
    }
  };

  const exportToPDF = () => {
    try {
      if (!patients || patients.length === 0) {
        toast.error('No patient records to export', { autoClose: 2000 });
        return;
      }

      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Patients Directory Report', pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const fromDateStr = fromDate ? new Date(fromDate).toLocaleDateString('en-GB') : 'All';
      const toDateStr = toDate ? new Date(toDate).toLocaleDateString('en-GB') : 'All';
      doc.text(`Date Range: ${fromDateStr} to ${toDateStr}`, pageWidth / 2, 22, { align: 'center' });
      doc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
      doc.text(`Total Patients: ${patients.length}`, pageWidth / 2, 34, { align: 'center' });

      // Prepare table data
      const tableData = patients.map(patient => [
        patient.PatientID || 'N/A',
        patient.PatientName || 'N/A',
        patient.MobileNo || 'N/A',
        patient.Place || 'N/A',
        patient.Age || 0,
        patient.Month || 0,
        patient.Days || 0,
        patient.DOB ? formatDateForExport(patient.DOB) : 'N/A',
        patient.Gender || 'N/A',
        patient.EntryDate ? formatDateForExport(patient.EntryDate) : 'N/A',
        patient.InvoiceNo || 'N/A',
        patient.Ref_DoctorName || 'N/A',
        patient.ValidUpto ? formatDateForExport(patient.ValidUpto) : 'N/A',
      ]);

      // Create table
      autoTable(doc, {
        head: [['ID', 'Name', 'Mobile', 'Place', 'Age', 'M', 'D', 'DOB', 'Gender', 'Entry Date', 'OP No', 'Doctor', 'Valid Upto']],
        body: tableData,
        startY: 40,
        styles: { 
          fontSize: 7, 
          cellPadding: 2,
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        headStyles: { 
          fillColor: [99, 102, 241], 
          textColor: 255, 
          fontStyle: 'bold',
          fontSize: 8
        },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { top: 40, left: 10, right: 10 },
        tableWidth: 'wrap',
      });

      // Generate filename
      const fromDateFile = fromDate ? new Date(fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
      const toDateFile = toDate ? new Date(toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
      const filename = `Patients_Report_${fromDateFile}_to_${toDateFile}.pdf`;

      // Save file
      doc.save(filename);
      toast.success('PDF file exported successfully', { autoClose: 2000 });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF', { autoClose: 2000 });
    }
  };

  return (
    <div className="flex h-full bg-background">
      {loading && <Spinner />}
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (tabContext) {
                    // Close current tab
                    if (tabContext.activeTabId && tabContext.closeTab) {
                      tabContext.closeTab(tabContext.activeTabId);
                    }
                  }
                }}
                className="mt-1 inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
              >
                <FiArrowLeft size={18} />
              </button>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                  Patient Management
                </p>
                <h1 className="text-lg font-bold text-slate-800 flex items-center">
                  <FiUser className="mr-2" size={20} />
                  Patients Directory
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleAddNewPatient}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                title="Add New Patient"
              >
                <FiPlus size={16} />
                Add New Patient
              </button>
              <button
                type="button"
                onClick={exportToExcel}
                className="inline-flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-600"
                title="Export to Excel"
              >
                <FiDownload size={16} />
                Excel
              </button>
              <button
                type="button"
                onClick={exportToPDF}
                className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-600"
                title="Export to PDF"
              >
                <FiDownload size={16} />
                PDF
              </button>
              <button
                type="button"
                onClick={handleSearch}
                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
              >
                <FiSearch size={16} />
                Search
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-hidden px-4 py-4 flex flex-col">
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Filters Section */}
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex-shrink-0">
              <div className="grid gap-4 md:grid-cols-2">
                {/* From Date */}
                <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                  From Date
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                  />
                </label>
                
                {/* To Date */}
                <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                  To Date
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    min={fromDate}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                  />
                </label>

                {/* Patient Search Dropdown */}
                <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                  Search Patient by Mobile/Name
                  <Select
                    placeholder="Enter mobile number or name to search..."
                    inputId="patient-search-input"
                    value={patientOptions.find(option => option.value === selectedSearchPatient?.PatientID)}
                    options={patientOptions}
                    formatOptionLabel={customOptionLabel}
                    onInputChange={(value, { action }) => {
                      if (action === 'input-change') {
                        handleMobileChange(value);
                      }
                    }}
                    onChange={(selectedOption) => {
                      handleSuggestionClick(selectedOption);
                    }}
                    isClearable
                    noOptionsMessage={() => "Type mobile number or name to search"}
                    styles={customSelectStyles}
                    className="w-full"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </label>
                
                {/* Text Search (for filtering displayed results) */}
                <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                  Filter Results
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black" size={18} />
                    <input
                      type="text"
                      placeholder="Filter by name, mobile, place, doctor, or OP number..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSelectedSearchPatient(null); // Clear selected patient when typing
                      }}
                      className="w-full pl-10 pr-3 py-2 border border-slate-200 bg-white rounded-lg text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                    />
                  </div>
                </label>
              </div>
            </section>
        
            {/* Patients Grid */}
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col flex-1 min-h-0">
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <div>
                  <p className="text-sm text-slate-500">
                    Showing <span className="font-semibold text-slate-700">{patients.length}</span> patients
                  </p>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto min-h-0">
                {patients.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {patients.map((patient, index) => (
                      <PatientCard
                        key={`${patient.PatientID}-${index}`}
                        patient={patient}
                        onClick={() => handlePatientClick(patient)}
                        onEdit={handleEditPatient}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-12">
                    <FiUser className="text-black mb-4" size={64} />
                    <p className="text-black font-medium text-lg">No patients found</p>
                    <p className="text-black text-sm mt-2">
                      {searchQuery ? 'Try adjusting your search criteria' : 'Select a date range to view patients'}
                    </p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </main>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={2000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {/* Edit Patient Modal */}
      {patientModal && (
        <AddPatientModal
          setPatientModal={setPatientModal}
          refreshData={refreshPatientData}
          mobileNumber={editPatient?.MobileNo || ''}
          selectedPatient={editPatient}
          onPatientSaved={handlePatientUpdated}
        />
      )}
    </div>
  );
}

export default withAuth(Patients);

